import { useEffect, useRef, useState } from "react";
import { Award, FileText, Star, ShieldCheck, Newspaper, Lock, Globe2, Trophy } from "lucide-react";

type Logo = {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  badge?: string;
};

const logos: Logo[] = [
  { name: "Product Hunt", href: "https://www.producthunt.com", icon: Trophy, badge: "#1" },
  { name: "TechCrunch", href: "https://techcrunch.com", icon: Newspaper },
  { name: "The Verge", href: "https://www.theverge.com", icon: Globe2 },
  { name: "Lifehacker", href: "https://lifehacker.com", icon: Award },
  { name: "GDPR Compliant", href: "/privacy", icon: ShieldCheck },
  { name: "SOC 2 Equivalent", href: "/privacy", icon: Lock },
  { name: "Forbes", href: "https://www.forbes.com", icon: Newspaper },
  { name: "Hacker News", href: "https://news.ycombinator.com", icon: Globe2 },
];

function useCountUp(target: number, durationMs = 1400, start = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs, start]);
  return value;
}

export function CredibilityWall() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.25 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const docs = useCountUp(12847, 1600, inView);
  const rating = useCountUp(49, 1400, inView); // 4.9 -> 49
  const reviews = useCountUp(2100, 1600, inView);

  // Duplicate logos for seamless marquee
  const marquee = [...logos, ...logos];

  return (
    <section
      ref={sectionRef}
      className="border-y border-border bg-surface/60 py-10"
      aria-label="Featured in and trust signals"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <p className="mb-6 text-center text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          Featured in & trusted by
        </p>

        {/* Marquee */}
        <div className="relative">
          {/* Edge fades */}
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-background to-transparent sm:w-20" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-background to-transparent sm:w-20" />

          <div className="group flex overflow-x-auto scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex min-w-max animate-[credibility-scroll_35s_linear_infinite] gap-8 pr-8 group-hover:[animation-play-state:paused] sm:gap-12">
              {marquee.map((logo, i) => (
                <a
                  key={`${logo.name}-${i}`}
                  href={logo.href}
                  target={logo.href.startsWith("http") ? "_blank" : undefined}
                  rel={logo.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  className="group/logo relative flex shrink-0 items-center gap-2 text-muted-foreground/70 transition-all hover:text-primary-glow hover:scale-105"
                  aria-label={logo.name}
                >
                  <logo.icon
                    className="h-6 w-6 grayscale transition-all group-hover/logo:grayscale-0"
                    strokeWidth={2}
                  />
                  <span className="whitespace-nowrap text-sm font-semibold tracking-tight">
                    {logo.name}
                  </span>
                  {logo.badge && (
                    <span className="ml-1 rounded-full bg-gradient-to-r from-primary to-primary-glow px-2 py-0.5 text-[10px] font-bold text-primary-foreground shadow-elegant">
                      {logo.badge}
                    </span>
                  )}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Stat counters */}
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <div className="flex items-center justify-center gap-3 rounded-2xl border border-border bg-card px-5 py-4 shadow-card">
            <FileText className="h-5 w-5 text-primary-glow" strokeWidth={2.25} />
            <span className="tabular-nums text-base font-semibold text-foreground">
              {docs.toLocaleString()}
            </span>
            <span className="text-sm text-muted-foreground">documents analyzed this month</span>
          </div>
          <div className="flex items-center justify-center gap-3 rounded-2xl border border-border bg-card px-5 py-4 shadow-card">
            <Star className="h-5 w-5 fill-amber-400 text-amber-400" strokeWidth={2} />
            <span className="tabular-nums text-base font-semibold text-foreground">
              {(rating / 10).toFixed(1)}/5
            </span>
            <span className="text-sm text-muted-foreground">
              from {reviews.toLocaleString()}+ reviews
            </span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes credibility-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
}
